import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from '@/api/queryKeys';
import { queryClient } from '@/config/reactQuery';
import {
  CompareStockReconciliation,
  StockReconciliationResult,
  StockReconciliationRowQuery,
} from '@/types/StockReconciliation';
import {
  unwrapStockReconciliationHistory,
  unwrapStockReconciliationQueue,
  unwrapStockReconciliationQueueItem,
  unwrapStockReconciliationResult,
  unwrapStockReconciliationRows,
} from '../unwrapResult';
import { stockReconciliation } from '../StockReconciliation';

export const useEnqueueStockReconciliation = () =>
  useMutation({
    mutationFn: async (data: CompareStockReconciliation) =>
      unwrapStockReconciliationQueueItem(
        await stockReconciliation.enqueue(data),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.stockReconciliationQueue],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.stockReconciliationHistory],
      });
    },
  });

export const useCompareStockReconciliation = useEnqueueStockReconciliation;

export const useSaveStockReconciliation = () =>
  useMutation({
    mutationFn: async (data: StockReconciliationResult) =>
      unwrapStockReconciliationResult(await stockReconciliation.save(data)),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.stockReconciliationHistory],
      }),
  });

export const useStockReconciliationQueue = (options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: [QUERY_KEYS.stockReconciliationQueue],
    queryFn: async () =>
      unwrapStockReconciliationQueue(await stockReconciliation.queue()),
    enabled: options?.enabled !== false,
    refetchInterval: 2000,
    retry: false,
  });

export const useStockReconciliationHistory = (
  params?: {
    customerId?: number;
    locationId?: number;
    skip?: number;
    take?: number;
  },
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: [QUERY_KEYS.stockReconciliationHistory, params],
    queryFn: async () =>
      unwrapStockReconciliationHistory(await stockReconciliation.list(params)),
    enabled: options?.enabled !== false,
    retry: false,
  });

export const useStockReconciliationRows = (
  id?: number,
  params?: StockReconciliationRowQuery,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: [QUERY_KEYS.stockReconciliationRows, id, params],
    queryFn: async () =>
      unwrapStockReconciliationRows(
        await stockReconciliation.rows(id as number, params!),
      ),
    enabled: Boolean(id) && options?.enabled !== false,
    placeholderData: keepPreviousData,
    retry: false,
  });

export const useStockReconciliationRun = (
  id?: number,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: [QUERY_KEYS.stockReconciliation, id],
    queryFn: async () =>
      unwrapStockReconciliationResult(
        await stockReconciliation.getById(id as number),
      ),
    enabled: Boolean(id) && options?.enabled !== false,
    retry: false,
  });
